/*
 * The map seam.
 *
 * A consumer asks for "a map with these markers" and gets one. Which vendor
 * draws it is `VUE_APP_MAP_PROVIDER`'s business, not the consumer's -- which
 * is what makes the vendor swappable by configuration instead of by editing
 * components, and what makes map interaction testable with no external
 * service. See docs/google-maps-to-osm.md.
 *
 * The contract both implementations honour:
 *
 *   props   center, zoom, markers, fitToMarkers, selectedMarkerId
 *   slot    #info   -- rendered in the popup over the selected marker
 *   events  marker-click(marker), map-click()
 *   DOM     the root element carries `vue-map-container` and
 *           `data-testid="marker-map"` under every provider
 *
 * That last line is load-bearing. `vue-map-container` is gmap-vue's class, and
 * both `ShowOnMapModal`'s scoped CSS and
 * qa/cypress/cypress/e2e/frontend/4_geoPages.spec.cy.ts:120 select on it. The
 * Leaflet implementation keeps it deliberately so neither has to change with
 * the provider.
 */
import { computed, defineComponent } from '@vue/composition-api';
import { MAP_PROVIDER } from '@/config/map';
import GoogleMarkerMap from '@/shared/components/map/GoogleMarkerMap.vue';
import LeafletMarkerMap from '@/shared/components/map/LeafletMarkerMap.vue';
export default defineComponent({
    components: {
        GoogleMarkerMap,
        LeafletMarkerMap
    },
    props: {
        center: {
            type: Object,
            required: true
        },
        zoom: {
            type: Number,
            default: 16
        },
        markers: {
            type: Array,
            default: () => []
        },
        /** Zoom and pan so every marker is in view. Ignored for a single marker. */
        fitToMarkers: {
            type: Boolean,
            default: false
        },
        /** Which marker's #info popup is open, or null for none. */
        selectedMarkerId: {
            type: Number,
            default: null
        }
    },
    setup() {
        // This line is the ONLY thing keeping `GoogleMarkerMap` off an OSM install,
        // and something in that component depends on never being mounted there: it
        // calls `Vue.$gmapApiPromiseLazy()` at the top of setup, unguarded, and
        // gmap-vue only installs that function when its plugin is registered --
        // which happens only when a Google key exists. Mount it under OSM and setup
        // throws `$gmapApiPromiseLazy is not a function` before a map ever renders.
        //
        // So GoogleMarkerMap is safe by construction, not by a guard. If this ever
        // becomes a render of both with a `v-if` inside, or if the component is
        // used anywhere that does not route through here, that call needs the guard
        // `NearbyMap` now has. The same shape already cost us `MapModal.vue`, which
        // carried it through a migration before anyone noticed nothing rendered it.
        const implementation = computed(() => MAP_PROVIDER === 'google' ? 'GoogleMarkerMap' : 'LeafletMarkerMap');
        return { implementation };
    }
});
